import { FunctionType } from './types';
import DOMPurify from 'dompurify';
import { gzip } from 'pako';
import { getWorkSpaceApiBearerToken } from './msalAPIITokenAcquire';

// Function to conditionally join class names
export const joinClassnames = (...classes: string[]) => {
  return classes.filter(Boolean).join(' ');
};

// Function to convert a number to a displayable string, adding commas every 3 digits
export const numberToDisplayableString = (number: number | undefined) => {
  return number !== undefined && number !== null ? number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '0';
};

// Function to provide a string preview of the first 200 characters and adding an ellipsis if the string is longer
export const previewString = (string: string) => {
  return string.length > 200 ? string.substring(0, 200) + '...' : string;
};

// Function to capitalize the first letter of every word in a string
export const capitalizeEveryWord = (string: string) => {
  return string
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const getPageUIDByName = (pages: any, name: string) => {
  let result;
  if (pages && pages.length > 0) {
    result = pages.filter((page: any) => {
      return page.ConfigurationName === name;
    });
    if (result && result.length > 0) {
      return result[0].ConfigurationUId;
    }
  }
};

export const getPromptIdFromName = (prompts: any, name: string) => {
  let result;
  if (prompts && prompts.length > 0) {
    result = prompts.filter((prompt: any) => {
      return prompt.PromptName === name;
    });
    if (result && result.length > 0) {
      return result[0].PredefinedPromptUId;
    } else {
      return null;
    }
  }
};

export const getConfigIdByName = (feedbacks: any, feedbackName: string) => {
  let result;
  if (feedbacks && feedbacks.length > 0) {
    result = feedbacks.filter((feedback: any) => {
      return feedback.ConfigurationName === feedbackName;
    });
    if (result && result.length > 0) {
      return result[0].ConfigurationUId;
    } else {
      return null;
    }
  }
};

export const getConfigIdByDescription = (feedbacks: any, feedbackDesccription: string) => {
  let result;
  if (feedbacks && feedbacks.length > 0) {
    result = feedbacks.filter((feedback: any) => {
      return feedback.ConfigurationDescription === feedbackDesccription;
    });
    if (result && result.length > 0) {
      return result[0].ConfigurationUId;
    } else {
      return null;
    }
  }
};

export const trackUserClicks = (
  websocket: any,
  pageName: string,
  userDetails: any,
  chatSessionId: string,
  WBUID: string,
  AppName: string,
  accounts: any,
  accessToken?: any,
  genArcId?: string,
  openAI = false,
  genAI = false,
  AccessVersionUId?: string,
  AccessVersion?: string
) => {
  if (userDetails && userDetails.GenARCAppUId && userDetails.GenARCAppUserUId.length > 0) {
    //UserTrackingSaveUserDetails
    const request: any = {
      function: 'save_user_details',
      userId: getUserIdFromLocalStorage(),
      GenARCAppUserUId: userDetails.GenARCAppUserUId,
      GenARCAppUId: userDetails.GenARCAppUId,
      ReferenceUId: genArcId ? genArcId : null,
      EntityUId:
        pageName === 'Like' || pageName === 'DisLike'
          ? getConfigIdByName(userDetails.ConfigurationDetails.Feedbacks, pageName)
          : getPageUIDByName(userDetails.ConfigurationDetails.Pages, pageName),
      SessionUId: chatSessionId,
      OpenAI: openAI,
      GenAI: genAI,
      WorkbenchUId: WBUID ? WBUID : null,
      AppName: AppName ? AppName : 'GMA',
      EnterpriseId: getUserEnterpriseId(accounts),
      AccessVersionUId:
        AccessVersionUId === 'Mobile' || AccessVersionUId === 'Laptop/Desktop'
          ? getConfigIdByName(userDetails.ConfigurationDetails.Pages, AccessVersionUId)
          : null,
      AccessVersion: AccessVersion ? AccessVersion : null,
      authorization: `Bearer ${accessToken}`,
    };
    console.log('reqObj for save_user_details', request);
    websocket?.send('SendMessage', request);
  }
};

export const fetchUserDetails = (accounts: any, websocket: any, accessToken: any) => {
  const request = {
    function: 'fetch_user_details',
    userId: getUserIdFromLocalStorage(),
    EnterpriseId: getUserEnterpriseId(accounts),
    AppKey: 'GMA',
    authorization: `Bearer ${accessToken}`,
  };
  console.log('reqObj for fetch_user_details', request);
  websocket?.send('SendMessage', request);
};

export const getUserEnterpriseId = (accounts: any) => {
  const isAuthenticated = accounts.length > 0;
  if (!isAuthenticated) {
    return '';
  } else {
    const username = accounts[0].username;
    const index = username?.indexOf('@');
    return username?.slice(0, index);
  }
};

export const getUserIdFromLocalStorage = () => {
  if (localStorage.getItem('gamma-userid')) {
    return localStorage.getItem('gamma-userid');
  } else {
    const newUserId = crypto.randomUUID();
    localStorage.setItem('gamma-userid', newUserId);
    return newUserId;
  }
};

export const getInitialsFromUName = (name: string): string => {
  const initials: string[] = name.replace(/\s/g, '').split(',');
  return initials
    .map((initial) => initial[0])
    .join('')
    .toUpperCase();
};

export const generateColor = (initials: string) => {
  // Simple hash function to generate a number based on initialslet hash = 0;
  let hash = 0;
  for (let i = 0; i < initials.length; i++) {
    hash = initials.charCodeAt(i) + ((hash << 5) - hash);
  }
  // Convert the hash into an RGB colorconst color = '#' + ((hash & 0xFFFFFF) << 0).toString(16).padStart(6, '0');
  const color = '#' + ((hash & 0xffffff) << 0).toString(16).padStart(6, '0');

  return color;
};

export const openLinkNewTab = (href: string) => {
  window.open(href, '_blank', 'noopener,noreferrer');
};

export const fetchProjectDetails = (accounts: any, websocket: any, accessToken: any): void => {
  const request = {
    function: FunctionType.FETCHPROJECTDETAILS,
    userId: getUserIdFromLocalStorage(),
    EnterpriseId: getUserEnterpriseId(accounts),
    authorization: `Bearer ${accessToken}`,
  };
  console.log('reqObj for fetch_project_details', request);

  websocket?.send('SendMessage', request);
};

export const fetchProjectNames = (accounts: any, websocket: any, accessToken: any): void => {
  const request = {
    function: FunctionType.GETPROJECT,
    userId: getUserIdFromLocalStorage(),
    EnterpriseId: getUserEnterpriseId(accounts),
    authorization: `Bearer ${accessToken}`,
  };
  console.log('reqObj for fetch_project_names', request);

  websocket?.send('SendMessage', request);
};

export const fetchAccountNames = (accounts: any, websocket: any, accessToken: any): void => {
  const request = {
    function: FunctionType.FETCHACCOUNTNAMES,
    userId: getUserIdFromLocalStorage(),
    EnterpriseId: getUserEnterpriseId(accounts),
    authorization: `Bearer ${accessToken}`,
  };
  console.log('reqObj for fetch_account_names', request);

  websocket?.send('SendMessage', request);
};

export const fetchIndustryNames = (accounts: any, websocket: any, accessToken: any): void => {
  const request = {
    function: FunctionType.FETCHINDUSTRYNAMES,
    userId: getUserIdFromLocalStorage(),
    EnterpriseId: getUserEnterpriseId(accounts),
    authorization: `Bearer ${accessToken}`,
  };
  console.log('reqObj for fetch_Industry_names', request);

  websocket?.send('SendMessage', request);
};

export const createNewProject = (accounts: any, websocket: any, form: any, accessToken: any): void => {
  const request = {
    function: FunctionType.CREATENEWPROJECT,
    userId: getUserIdFromLocalStorage(),
    EnterpriseId: getUserEnterpriseId(accounts),
    account_name: form?.accountName,
    project_name: form?.projectName,
    industry_vertical: form?.industryName,
    project_begin_date: form?.startDate,
    project_end_date: form?.endDate,
    authorization: `Bearer ${accessToken}`,
  };
  console.log('reqObj for  createNewProject', request);

  websocket?.send('SendMessage', request);
};

export const fetchChatHistory = (accounts: any, websocket: any, accessToken: any): void => {
  const request = {
    function: FunctionType.FETCHCHATHISTORY,
    userId: getUserIdFromLocalStorage(),
    EnterpriseId: getUserEnterpriseId(accounts),
    authorization: `Bearer ${accessToken}`,
  };
  console.log('reqObj for fetch_chat_history', request);

  websocket?.send('SendMessage', request);
};

export const updateChatHistory = (
  accounts: any,
  websocket: any,
  chatSessionId: string,
  displayName: string,
  isDeleted: boolean,
  genArcPromptAnalyticsUID: string,
  accessToken: any
): void => {
  const request = {
    function: FunctionType.UPDATECHATHISTORY,
    EnterpriseId: getUserEnterpriseId(accounts),
    SessionUId: chatSessionId,
    userId: getUserIdFromLocalStorage(),
    displayname: displayName,
    isdeleted: isDeleted,
    GenArcPromptAnalyticsUId: genArcPromptAnalyticsUID,
    authorization: `Bearer ${accessToken}`,
  };
  console.log('reqObj for update_chat_history', request);

  websocket?.send('SendMessage', request);
};

export const deleteChatHistory = (
  accounts: any,
  websocket: any,
  chatSessionId: string,
  displayName: string,
  isDeleted: boolean,
  genArcPromptAnalyticsUID: string,
  accessToken: any
): void => {
  const request = {
    function: FunctionType.DELETECHATHISTORY,
    EnterpriseId: getUserEnterpriseId(accounts),
    SessionUId: chatSessionId,
    userId: getUserIdFromLocalStorage(),
    displayname: displayName,
    isdeleted: isDeleted,
    GenArcPromptAnalyticsUId: genArcPromptAnalyticsUID,
    authorization: `Bearer ${accessToken}`,
  };
  console.log('reqObj for delete_chat_history', request);

  websocket?.send('SendMessage', request);
};

export const fetchHistoryFromBlob = (
  accounts: any,
  websocket: any,
  fileName: string,
  genArcPromptAnalyticsUID: string,
  accessToken: any
): void => {
  const request = {
    function: FunctionType.FETCHHISTORYFROMBLOB,
    userId: getUserIdFromLocalStorage(),
    fileName: fileName,
    GenArcPromptAnalyticsUId: genArcPromptAnalyticsUID,
    EnterpriseId: getUserEnterpriseId(accounts),
    authorization: `Bearer ${accessToken}`,
  };
  console.log('reqObj for fetch_history_from_blob', request);

  websocket?.send('SendMessage', request);
};

// Function to check XSS vulnerability and return sanitized string
export const validateStringForXSS = (str: string) => {
  return DOMPurify.sanitize(str).trim();
};

export const FetchSupportServiceNow = (accounts: any, websocket: any, accessToken: any) => {
  const request = {
    function: FunctionType.FETCHSUPPORTSERVICENOW,
    userId: getUserIdFromLocalStorage(),
    EnterpriseId: getUserEnterpriseId(accounts),
    AppKey: 'GMA',
    authorization: `Bearer ${accessToken}`,
  };
  console.log('reqObj for fetch_supportservicenow', request);
  websocket?.send('SendMessage', request);
};

export const copyToClipboard = (htmlNode: HTMLElement): Promise<string> => {
  const promise = new Promise<string>((resolve, reject) => {
    // Select the content
    const range = document.createRange();
    range.selectNodeContents(htmlNode);
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }

    // Copy the selected content (preserves formatting)
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        resolve('Text copied to clipboard');
      } else {
        reject('Oops, unable to copy');
      }
    } catch (err) {
      reject('Oops, unable to copy');
      // console.error('Error copying content:', err);
    }

    // Cleanup: Deselect
    if (selection) {
      selection.removeAllRanges();
    }
  });
  return promise;
};

export const fetchSurveyMaster = (accounts: any, websocket: any, accessToken: any) => {
  const request = {
    function: FunctionType.FETCHSURVEYMASTER,
    userId: getUserIdFromLocalStorage(),
    EnterpriseId: getUserEnterpriseId(accounts),
    authorization: `Bearer ${accessToken}`,
  };
  console.log('reqObj for fetch_survey_master', request);
  websocket?.send('SendMessage', request);
};

export const removeMarkTag = (str: string) => {
  return str.replaceAll(/<\/?mark\b[^>]*>/gi, '');
};

// escape single quote from prompt(postgres sql query technicalities)
export const addDoubleApostrophe = (str: string) => {
  return str.replaceAll(/'/g, "''");
};

export const handleFileDownloadWithURL = (url: string, ...callbacks: any) => {
  if (window.self === window.top) {
    window.open(url, '_self');
  } else {
    window.open(url, '_blank');
  }
  callbacks.map((cb: any) => {
    if (typeof cb === 'function') {
      cb();
    }
  });
  // Reset the exportedStoryURL and exportStoryInProgress
};

export const fetchInterviewProjectDetails = (accounts: any, websocket: any, accessToken: any): void => {
  const request = {
    function: FunctionType.GETPROJECT,
    userId: getUserIdFromLocalStorage(),
    EnterpriseId: getUserEnterpriseId(accounts),
    authorization: `Bearer ${accessToken}`,
  };
  console.log('reqObj for get_project', request);

  websocket?.send('SendMessage', request);
};

export const fetchInterviewCountryDetails = (accounts: any, websocket: any, accessToken: any): void => {
  const request = {
    function: FunctionType.GETCOUNTRY,
    userId: getUserIdFromLocalStorage(),
    EnterpriseId: getUserEnterpriseId(accounts),
    authorization: `Bearer ${accessToken}`,
  };
  console.log('reqObj for get_country', request);

  websocket?.send('SendMessage', request);
};

export const fetchInterviewVenueDetails = (accounts: any, websocket: any, accessToken: any): void => {
  const request = {
    function: FunctionType.GETVENUE,
    userId: getUserIdFromLocalStorage(),
    EnterpriseId: getUserEnterpriseId(accounts),
    authorization: `Bearer ${accessToken}`,
  };
  console.log('reqObj for get_venue', request);

  websocket?.send('SendMessage', request);
};

export const fetchInterviewProductDetails = (accounts: any, websocket: any, accessToken: any): void => {
  const request = {
    function: FunctionType.GETPRODUCT,
    userId: getUserIdFromLocalStorage(),
    EnterpriseId: getUserEnterpriseId(accounts),
    authorization: `Bearer ${accessToken}`,
  };
  console.log('reqObj for get_product', request);

  websocket?.send('SendMessage', request);
};

const gzipToBase64 = (text: string): string => {
  const gzipped = gzip(text); // Uint8Array (valid gzip binary)
  let binary = '';

  gzipped.forEach((byte) => (binary += String.fromCharCode(byte)));

  return btoa(binary); // convert to Base64
};

export const createNewInterviewPrimary = (accounts: any, websocket: any, form: any, accessToken: any): void => {
  const compressedTranscript = gzipToBase64(form?.transcript || '');

  const request = {
    function: FunctionType.CREATENEWINTERVIEW,
    userId: getUserIdFromLocalStorage(),
    LoggedInUser: getUserEnterpriseId(accounts),
    username: getUserEnterpriseId(accounts),
    Country: form?.selectedCountries,
    InterviewTranscript: compressedTranscript,
    Project: form?.selectedProject,
    Product: form?.selectedProducts,
    Venue: form?.selectedVenues,
    ResearchType: 1,
    Company: form?.companyName,
    'Job title': form?.jobTitle,
    InterviewDate: form?.interviewDate,
    authorization: `Bearer ${accessToken}`,
  };
  console.log('reqObj for  new_interview primary', request);

  websocket?.send('SendMessage', request);
};

export const createNewInterviewSecondary = (accounts: any, websocket: any, form: any, accessToken: any): void => {
  const compressedTranscript = gzipToBase64(form?.transcript || '');

  const request = {
    function: FunctionType.CREATENEWINTERVIEW,
    userId: getUserIdFromLocalStorage(),
    LoggedInUser: getUserEnterpriseId(accounts),
    username: getUserEnterpriseId(accounts),
    Country: form?.selectedCountries,
    InterviewTranscript: compressedTranscript,
    Project: form?.selectedProject,
    Product: form?.selectedProducts,
    Venue: form?.selectedVenues,
    ResearchType: 2,
    AccessTitle: form?.articleTitle,
    Publication: form?.publication,
    PublicationDate: form?.publicationDate,
    AccessDate: form?.accessDate,
    ArticleURL: form?.articleURL,
    authorization: `Bearer ${accessToken}`,
  };
  console.log('reqObj for  new_interview secondary', request);

  websocket?.send('SendMessage', request);
};

export const fetchUploadedFileDetails = (accounts: any, websocket: any, accessToken: any): void => {
  const request = {
    function: FunctionType.GETUPLOADEDFILEDETAILS,
    userId: getUserIdFromLocalStorage(),
    EnterpriseId: getUserEnterpriseId(accounts),
    authorization: `Bearer ${accessToken}`,
  };
  console.log('reqObj for get_file_status', request);

  websocket?.send('SendMessage', request);
};

const base64ToBlob = (b64Data: string, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    byteArrays.push(new Uint8Array(byteNumbers));
  }

  return new Blob(byteArrays, { type: contentType });
};
export const handleExcelDownloadResponse = (response: any) => {
  const { FileName, FileContent } = response; // fileContent = Base64

  if (!FileContent) return;

  const blob = base64ToBlob(FileContent, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = FileName || 'report.xlsx';
  link.click();

  URL.revokeObjectURL(url);
};

export const downloadFileTemplate = (accounts: any, websocket: any, accessToken: any): void => {
  const request = {
    function: FunctionType.DOWNLOADTEMPLATE, // <-- Define this enum in FunctionType
    userId: getUserIdFromLocalStorage(),
    EnterpriseId: getUserEnterpriseId(accounts),
    authorization: `Bearer ${accessToken}`,
  };

  console.log('reqObj for download_excel', request);

  websocket?.send('SendMessage', request);
};

export const fetchSearchTranscriptDetails = (
  accounts: any,
  websocket: any,
  title: any,
  selectedProject: any,
  openAI: any,
  genAI: any,
  selectedTranscript: any,
  prompts: any,
  genArcPromptAnalyticsUId: string,
  isPreDefinedPrompt?: boolean,
  accessToken?: any
): void => {
  const request = {
    function: 'search',
    prompt: title,
    userId: getUserIdFromLocalStorage(),
    LoggedInUser: getUserEnterpriseId(accounts),
    PreDefinedPromptUId: getPromptIdFromName(prompts, title),
    UserPrompt: isPreDefinedPrompt ? null : title,
    OpenAI: openAI,
    GenAI: genAI,
    projectName: selectedProject,
    GenArcPromptAnalyticsUId: genArcPromptAnalyticsUId,
    sourceName: selectedTranscript,
    authorization: `Bearer ${accessToken}`,
  };

  console.log('reqObj for Search', request);
  websocket.send('SendMessage', request);
};

export const getlogoutFlag = (websocket: any, accounts: any, accessToken: any) => {
  const payload = {
    function: 'logout_token',
    userId: getUserIdFromLocalStorage(),
    EnterpriseId: getUserEnterpriseId(accounts),
    authorization: `Bearer ${accessToken}`,
  };
  console.log('payload for request logout: ', payload);
  websocket?.send('SendMessage', payload);
};

export const handleKeyPress = (e: any) => {
  const regex = /^[a-zA-Z0-9 _-]*$/;
  if (!regex.test(e.key)) {
    e.preventDefault();
  }
};

const KNOWN_HTML_TAGS = new Set([
  'html',
  'head',
  'body',
  'title',
  'meta',
  'link',
  'style',
  'script',
  'noscript',
  'section',
  'nav',
  'article',
  'aside',
  'header',
  'footer',
  'main',
  'div',
  'span',
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'a',
  'ul',
  'ol',
  'li',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'td',
  'th',
  'caption',
  'form',
  'input',
  'textarea',
  'button',
  'select',
  'option',
  'label',
  'fieldset',
  'legend',
  'img',
  'picture',
  'source',
  'figure',
  'figcaption',
  'svg',
  'canvas',
  'video',
  'audio',
  'source',
  'iframe',
  'embed',
  'object',
  'param',
  'blockquote',
  'pre',
  'code',
  'em',
  'strong',
  'small',
  'sub',
  'sup',
  'b',
  'i',
  'u',
  'br',
  'hr',
  'template',
  'slot',
]);

export function validateTranscriptInput(input: string): boolean {
  const rx = /<\s*([^>]*?)\s*>/g;
  let m: RegExpExecArray | null;

  while ((m = rx.exec(input)) !== null) {
    const inner = m[1];
    const innerTrim = inner.trim();

    if (innerTrim === '') {
      return true;
    }

    if (/^\/\s*$/.test(inner)) {
      return true;
    }

    if (/^[!?]/.test(innerTrim)) {
      return true;
    }

    const afterSlash = innerTrim.replace(/^\/\s*/, '');
    const firstToken = afterSlash.split(/\s+/)[0];
    if (!firstToken) return true;

    const tagName = firstToken.toLowerCase();

    if (KNOWN_HTML_TAGS.has(tagName)) {
      return true;
    }
  }

  return false;
}

let tokenCache: string | null = null;
let tokenUpdateCallback: ((token: string) => void) | null = null;

// Register a callback to update state when token changes
export const registerTokenUpdateCallback = (callback: (token: string) => void) => {
  tokenUpdateCallback = callback;
};

// Get a fresh token and update cache
export const getValidToken = async (): Promise<string> => {
  const tokenScope = `api://${process.env.REACT_APP_CLIENT_CODE}/user_impersonation`;
  const tokenResponse = await getWorkSpaceApiBearerToken(tokenScope);
  const accessToken = tokenResponse?.accessToken;
  if (!accessToken) {
    throw new Error('Failed to acquire access token');
  }
  tokenCache = accessToken;
  // Notify registered callback (for state updates)
  if (tokenUpdateCallback) {
    tokenUpdateCallback(accessToken);
  }
  return accessToken;
};
// Get cached token without refreshing
export const getCachedToken = (): string | null => {
  return tokenCache;
};
